/**
 * Gerador de CSV modelo para importação de Negócios.
 *
 * Fornece um arquivo de exemplo alinhado ao autodetect do Step 2, com
 * cabeçalhos em português e algumas linhas demo usando origens válidas
 * (ids da constante LEAD_SOURCES). Usado pelo botão "Baixar CSV modelo"
 * no Step 1 do wizard.
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v1.1.0 - Todas as colunas (pipeline, fase, vendedor)
 *            agora vêm preenchidas com exemplos plausíveis para o usuário
 *            entender o formato esperado. Valores não encontrados caem no
 *            padrão definido no Step 3 do wizard.
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial.
 */

import { LEAD_SOURCES } from '@/lib/lead-sources';

/** Escapa um valor para CSV (separador `;`). */
function csvEscape(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Gera um Blob CSV (UTF-8 com BOM) com 3 linhas de exemplo.
 * Usa apenas origens reconhecidas pelo sistema para que o arquivo passe
 * pela validação contra `lead_sources`.
 */
export function buildSampleDealsCsv(): Blob {
  // Cabeçalhos alinhados ao AUTODETECT (steps/types.ts)
  const headers = [
    'titulo',
    'telefone',
    'nome',
    'email',
    'valor',
    'origem',
    'descricao',
    'pipeline',
    'fase',
    'vendedor',
    'tags',
  ];

  // Escolhe até 3 origens válidas do sistema para as linhas demo
  const demoSources = LEAD_SOURCES.slice(0, 3).map((s) => s.id);

  // Observação: pipeline/fase/vendedor aceitam nome (case-insensitive) ou
  // email do vendedor. Se o valor informado não existir no tenant, a
  // importação cai no padrão escolhido no Step 3 do wizard.
  const rows: string[][] = [
    [
      'Proposta Site - João Silva',
      '11987654321',
      'João Silva',
      'joao@exemplo.com',
      '1500.00',
      demoSources[0] ?? 'site',
      'Cliente interessado em plano premium',
      'Vendas',
      'Qualificação',
      'Maria Silva',
      'vip;recompra',
    ],
    [
      'Lead Instagram - Maria Souza',
      '21999887766',
      'Maria Souza',
      'maria@exemplo.com',
      '890,50',
      demoSources[1] ?? 'instagram',
      'Veio pelo anúncio de stories',
      'Vendas',
      'Proposta',
      'joao.santos@empresa.com',
      'organico',
    ],
    [
      'WhatsApp - Carlos Mendes',
      '+55 31 98888 7777',
      'Carlos Mendes',
      'carlos@exemplo.com',
      '2300',
      demoSources[2] ?? 'whatsapp',
      'Pediu retorno na quarta-feira',
      'Pós-Venda',
      'Negociação',
      'Carlos Lima',
      'parceiro',
    ],
  ];

  const lines = [
    headers.join(';'),
    ...rows.map((r) => r.map(csvEscape).join(';')),
  ];

  // BOM para Excel reconhecer UTF-8 corretamente
  return new Blob([`\uFEFF${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
}

/** Dispara download do CSV modelo no navegador. */
export function downloadSampleDealsCsv(filename = 'modelo-importacao-negocios.csv') {
  const blob = buildSampleDealsCsv();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
